"use client"
import React, { useState, useRef, useCallback } from "react"
import { Upload, Loader } from "lucide-react"
import { Worker, Viewer } from "@react-pdf-viewer/core"
import "@react-pdf-viewer/core/lib/styles/index.css"
import "@react-pdf-viewer/default-layout/lib/styles/index.css"
import { postAws } from "../postAws"
import { postDemoAccountFileStudyBuddy } from "../postFileAws"

export default function LandingDemoAssignment({ assignmentDemoUsed, setAssignmentDemoUsed }) {
  const [assignmentTitle, setAssignmentTitle] = useState("")
  const [assignmentClass, setAssignmentClass] = useState("")
  const [assignmentSubject, setAssignmentSubject] = useState("")
  const [questionCounts, setQuestionCounts] = useState({
    mcq: 0,
    short: 0,
    essay: 0,
  })
  const [difficulty, setDifficulty] = useState("medium")
  const [userEmail, setUserEmail] = useState("")
  const [assignmentFormError, setAssignmentFormError] = useState(null)
  const [isAssignmentSubmitting, setIsAssignmentSubmitting] = useState(false)
  const [showApiSuccessMessage, setShowApiSuccessMessage] = useState(false) // New state for API success message
  // Local state for PDF preview within Assignment demo
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null)
  const [uploadedS3Uri, setUploadedS3Uri] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const fileInputRef = useRef(null)

  const handleQuestionCountChange = (type, value) => {
    setQuestionCounts((prev) => ({
      ...prev,
      [type]: Math.max(0, Number.parseInt(value) || 0),
    }))
  }

  const handleFileChange = useCallback(async (event) => {
    const file = event.target.files[0]
    if (!file) {
      return
    }
    if (file.type !== "application/pdf") {
      setUploadError("Please upload a PDF file.")
      setPdfPreviewUrl(null)
      setUploadedS3Uri(null)
      event.target.value = null
      return
    }
    setUploadError(null)
    setIsUploading(true)
    const localPdfUrl = URL.createObjectURL(file)
    setPdfPreviewUrl(localPdfUrl)
    try {
      const uuidCreated = Date.now().toString()
      const filename = file.name
      const uploadResult = await postDemoAccountFileStudyBuddy(file, uuidCreated, filename)
      if (uploadResult && uploadResult.uri) {
        setUploadedS3Uri(uploadResult.uri)
      } else {
        setUploadError("Failed to upload file to S3.")
        setPdfPreviewUrl(null)
      }
    } catch (err) {
      console.error("Upload error:", err)
      setUploadError("An error occurred during upload.")
      setPdfPreviewUrl(null)
    } finally {
      setIsUploading(false)
      event.target.value = null
    }
  }, [])

  const triggerFileInput = () => {
    fileInputRef.current.click()
  }

  const handleAssignmentSubmit = async (e) => {
    e.preventDefault()
    setAssignmentFormError(null)
    setShowApiSuccessMessage(false) // Reset success message display
    setIsAssignmentSubmitting(true)

    if (assignmentDemoUsed) {
      setAssignmentFormError("You've used your free Assignment demo. Kindly login/signup to get more.")
      setIsAssignmentSubmitting(false)
      return
    }

    const totalQuestions = Object.values(questionCounts).reduce((sum, count) => sum + count, 0)
    if (!assignmentTitle || totalQuestions === 0 || !userEmail || !assignmentClass || !assignmentSubject) {
      setAssignmentFormError(
        "Please fill all required fields (Title, Class, Subject, Number of Questions, Email) and specify at least one question.",
      )
      setIsAssignmentSubmitting(false)
      return
    }

    if (!/\S+@\S+\.\S+/.test(userEmail)) {
      setAssignmentFormError("Please enter a valid email address.")
      setIsAssignmentSubmitting(false)
      return
    }

    let finalReferenceMaterialUri = uploadedS3Uri
    if (!finalReferenceMaterialUri && fileInputRef.current && fileInputRef.current.files[0]) {
      const file = fileInputRef.current.files[0]
      if (file.type !== "application/pdf") {
        setAssignmentFormError("Reference material must be a PDF file.")
        setIsAssignmentSubmitting(false)
        return
      }
      try {
        setIsUploading(true) // Indicate upload in progress for this specific file
        const uuidCreated = Date.now().toString()
        const filename = file.name
        const uploadResult = await postDemoAccountFileStudyBuddy(file, uuidCreated, filename)
        finalReferenceMaterialUri = uploadResult.uri
        setUploadedS3Uri(uploadResult.uri) // Store for potential re-use
      } catch (uploadErr) {
        setAssignmentFormError("Failed to upload reference material: " + uploadErr.message)
        setIsAssignmentSubmitting(false)
        setIsUploading(false)
        return
      } finally {
        setIsUploading(false)
      }
    }

    try {
      const response = await postAws("https://api-dev.aurait.ai/LandingPageDemo", "POST", {
        assignmentTitle: assignmentTitle,
        questionCounts: questionCounts,
        class: assignmentClass,
        subject: assignmentSubject,
        difficulty: difficulty,
        userEmail: userEmail,
        referenceMaterial: finalReferenceMaterialUri || null,
      })
      const responseBody = JSON.parse(response.body)
      console.log("Assignment API Response:", response)
      if (response.statusCode === 202) {
        setShowApiSuccessMessage(true) // Show the success message
        localStorage.setItem("assignmentDemoUsed", "true") // Mark Assignment demo as used
        setAssignmentDemoUsed(true) // Update parent state
      } else {
        throw new Error(responseBody.message || "Failed to create assignment.")
      }
    } catch (err) {
      console.error("Error creating assignment:", err)
      setAssignmentFormError(err.message || "An error occurred during assignment creation. Please try again.")
    } finally {
      setIsAssignmentSubmitting(false)
    }
  }

  if (assignmentDemoUsed) {
    return (
      <div className="flex flex-col items-center justify-center h-[600px] bg-gradient-to-br from-blue-50 via-white to-gray-100 border border-blue-100 rounded-2xl shadow-lg p-10 text-center">
        <div className="bg-white shadow-md rounded-xl px-8 py-10 max-w-xl">
          <h4 className="text-2xl font-bold text-gray-800 mb-4">📬 Assignment is on its way!</h4>
          <p className="text-gray-600 text-lg leading-relaxed">
            Your assignment will be mailed to you within <span className="font-semibold text-blue-600">2 minutes</span>.
            <br />
            To create more assignments, please log in to your account.
          </p>
          <button className="mt-6 bg-blue-600 hover:bg-blue-700 text-white text-lg font-medium px-8 py-3 rounded-full transition-all shadow-md hover:shadow-lg">
            Login / Sign Up
          </button>
        </div>
      </div>
    )
  }

  if (showApiSuccessMessage) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center h-[600px] border border-gray-200 rounded-lg bg-gray-50">
        <h4 className="text-xl font-semibold text-gray-700 mb-4">Assignment Creation Initiated!</h4>
        <p className="text-gray-600">Your assignment will be sent to you through mail in 5 mins.</p>
      </div>
    )
  }

  return (
    <div className="grid lg:grid-cols-2 gap-8 h-[600px]">
      {/* Left side: PDF Viewer (or placeholder if no PDF) */}
      <div className="flex-1 border border-gray-200 rounded-lg overflow-hidden h-full flex items-center justify-center bg-gray-100 text-gray-500">
        {isUploading ? (
          <div className="flex flex-col items-center justify-center">
            <Loader className="w-12 h-12 text-blue-600 animate-spin mb-4" />
            <h4 className="text-lg font-semibold text-gray-700">Uploading Reference Material...</h4>
            <p className="text-gray-500 text-sm">Please wait, your document is being processed.</p>
          </div>
        ) : pdfPreviewUrl ? (
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
            <Viewer fileUrl={pdfPreviewUrl} />
          </Worker>
        ) : (
          <div className="text-center p-4">
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p>Upload a PDF to use as reference material for your assignment.</p>
            <input type="file" ref={fileInputRef} className="hidden" accept=".pdf" onChange={handleFileChange} />
            <label
              htmlFor="pdf-upload"
              onClick={triggerFileInput}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors cursor-pointer inline-block mt-4"
            >
              Choose File
            </label>
            {uploadError && <p className="text-red-500 text-sm mt-4">{uploadError}</p>}
          </div>
        )}
      </div>
      {/* Right side: Assignment Form */}
      <div className="flex-1 flex flex-col border border-gray-200 rounded-lg overflow-hidden bg-gray-50 p-6">
        <div className="p-4 border-b border-gray-200 text-center">
          <h4 className="text-xl font-bold text-blue-600">Generate Assignment</h4>
        </div>
        <form onSubmit={handleAssignmentSubmit} className="flex-1 flex flex-col space-y-4 overflow-y-auto p-4">
          <div>
            <label htmlFor="assignmentTitle" className="block text-sm font-medium text-gray-700 text-left mb-1">
              Assignment Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="assignmentTitle"
              value={assignmentTitle}
              onChange={(e) => setAssignmentTitle(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label htmlFor="assignmentClass" className="block text-sm font-medium text-gray-700 text-left mb-1">
              Class <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="assignmentClass"
              value={assignmentClass}
              onChange={(e) => setAssignmentClass(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label htmlFor="assignmentSubject" className="block text-sm font-medium text-gray-700 text-left mb-1">
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="assignmentSubject"
              value={assignmentSubject}
              onChange={(e) => setAssignmentSubject(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 text-left mb-1">
              Number of Questions by Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 items-center bg-white p-3 rounded-md border border-gray-200">
              <div className="font-semibold text-gray-800 text-left">Question Type</div>
              {/* FIX: Added w-20 to align 'Count' header with input boxes */}
              <div className="font-semibold text-gray-800 text-right w-20">Count</div>
              {["mcq", "short", "essay"].map((type) => (
                <React.Fragment key={type}>
                  <div className="text-gray-700 text-left capitalize">{type.replace("-", " ")}</div>
                  <input
                    type="number"
                    value={questionCounts[type]}
                    onChange={(e) => handleQuestionCountChange(type, e.target.value)}
                    min="0"
                    className="w-20 p-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-right"
                  />
                </React.Fragment>
              ))}
            </div>
            {Object.values(questionCounts).reduce((sum, count) => sum + count, 0) === 0 && (
              <p className="text-red-500 text-xs mt-1 text-left">Please specify at least one question.</p>
            )}
          </div>
          <div>
            <label htmlFor="difficulty" className="block text-sm font-medium text-gray-700 text-left mb-1">
              Difficulty Level
            </label>
            <select
              id="difficulty"
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
          <div className="text-sm text-gray-600 text-left">
            Reference Material:{" "}
            {pdfPreviewUrl ? <span className="font-medium text-blue-600">PDF uploaded</span> : "None (upload on left)"}
          </div>
          <div>
            <label htmlFor="userEmail" className="block text-sm font-medium text-gray-700 text-left mb-1">
              Your Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="userEmail"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Where to send the answers"
              required
            />
          </div>
          {assignmentFormError && <p className="text-red-500 text-sm mt-2 text-left">{assignmentFormError}</p>}
          <button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md transition-colors text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed mt-4"
            disabled={isAssignmentSubmitting}
          >
            {isAssignmentSubmitting ? "Generating..." : "Generate Assignment"}
          </button>
        </form>
      </div>
    </div>
  )
}
